'use client';

import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ZapIcon } from 'components/ring-air/ZapIcon';
import type {
  SddArrivesWhen,
  SddDeliveryWindow,
  SddNddController,
  SddNddState,
} from './useSddNddEligibility';
import { sddTimerDeadline, sddWindowShowsTimer } from './useSddNddEligibility';
import styles from './index.module.css';

/** Fallback when API omits order_by: 19:00 IST. SAME_DAY and TWO_HOUR. */
const IST_OFFSET_MS = 330 * 60_000;
const TIMER_WINDOW_END_S = 19 * 3600;

/** main exported this as a styled.div; consumers only ever pass children. */
export const SddDeliverySlot = ({
  children,
}: {
  children?: React.ReactNode;
}) => <div className={styles.slot}>{children}</div>;

/* Lives inside the grey card, under the input row (design: 12px muted). */

/* One left→right pass, then the last keyframe holds (highlight off-canvas). */

/**
 * Fill-only shimmer for the eligible title (never idle / ineligible / error).
 * One-shot: letterforms never move, zero CLS, no JS. @supports is load-bearing
 * — without background-clip, transparent text would vanish. Reduced motion
 * keeps the solid title. Restart by remounting this span only (key=title),
 * never the Zap icon beside it.
 */

const pad2 = (value: number): string => String(value).padStart(2, '0');

/**
 * "Order within HH:MM:SS" to API `order_by`, or 19:00 IST when that key is
 * missing. Hidden once the deadline has passed (NEXT_DAY does not mount
 * this). Owns its own 1s interval so only this span re-renders per tick.
 * Remaining time is recomputed from the wall clock (no accumulated drift;
 * visibilitychange resnaps after background-tab throttling). Server render
 * and the first client paint show nothing, avoiding a hydration mismatch.
 */
const SddCountdown = ({ deadline }: { deadline?: string }) => {
  const t = useTranslations('ring');
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    const compute = (): number | null => {
      if (deadline) {
        const end = Date.parse(deadline);
        if (!Number.isNaN(end)) {
          const left = Math.floor((end - Date.now()) / 1000);
          return left > 0 ? left : null;
        }
      }
      const istSecondsOfDay =
        Math.floor((Date.now() + IST_OFFSET_MS) / 1000) % 86_400;
      if (istSecondsOfDay >= TIMER_WINDOW_END_S) return null;
      return TIMER_WINDOW_END_S - istSecondsOfDay;
    };
    const update = () => setSecondsLeft(compute());
    update();
    const id = setInterval(update, 1000);
    document.addEventListener('visibilitychange', update);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', update);
    };
  }, [deadline]);

  if (secondsLeft === null) return null;

  const hours = Math.floor(secondsLeft / 3600);
  const minutes = Math.floor((secondsLeft % 3600) / 60);
  const seconds = secondsLeft % 60;

  return (
    <span>
      <strong>{t('ring.sddNdd.orderWithin')}</strong>{' '}
      <span className={styles.timerDigits}>
        {pad2(hours)}:{pad2(minutes)}:{pad2(seconds)}
      </span>
    </span>
  );
};

/** Exclamation-in-circle (per design: the info glyph flipped vertically —
 * stem on top, dot at the bottom). */
const AlertIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" aria-hidden>
    <circle cx="7" cy="7" r="6.3" stroke="currentColor" strokeWidth="1" />
    <path
      d="M7 4V7.6"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <circle cx="7" cy="9.8" r="0.75" fill="currentColor" />
  </svg>
);

/** "Get it in 2 hours. In select cities." → medium lead sentence, regular rest. */
const splitTitle = (title: string): [string, string] => {
  const index = title.indexOf('. ');
  if (index === -1) return [title, ''];
  return [title.slice(0, index + 1), title.slice(index + 2)];
};

/** Titles are API copy without guaranteed punctuation; design ends them
 * with a period ("2-hour delivery available. Order within …"). */
const ensurePeriod = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed || /[.!?]$/.test(trimmed)) return trimmed;
  return `${trimmed}.`;
};

/** Same type as warehouse ShippingLabel (1.4rem) without its fade-on-remount. */

/** Same type as warehouse "Dispatched by" (1.4rem, date at 500). */
export const SddHeroEstimate = ({
  date,
  pincode,
  when,
  time,
  window: etaWindow,
}: {
  date: string;
  pincode: string;
  when: SddArrivesWhen;
  time?: string;
  window: SddDeliveryWindow;
}) => {
  const t = useTranslations('ring');
  if (etaWindow === 'two_hour') {
    return (
      <span className={styles.timelineLabel}>
        {t('ring.sddNdd.arrivesLead')}{' '}
        {time ? (
          <>
            {t('ring.sddNdd.byWord')} <strong>{time}</strong>
          </>
        ) : (
          <strong>{t('ring.sddNdd.twoHoursWord')}</strong>
        )}{' '}
        {t('ring.sddNdd.atPincode', { pincode })}
      </span>
    );
  }
  return (
    <span className={styles.timelineLabel}>
      {t('ring.sddNdd.arrivesLead')}{' '}
      {when === 'today'
        ? t('ring.sddNdd.todayWord')
        : t('ring.sddNdd.tomorrowWord')}
      , <strong>{date}</strong> {t('ring.sddNdd.atPincode', { pincode })}
    </span>
  );
};

const ineligibleMetaCopy = (
  window: SddDeliveryWindow,
  t: (key: string) => string,
): string => {
  if (window === 'two_hour') return t('ring.sddNdd.ineligibleSameDayWindow');
  if (window === 'next_day') return t('ring.sddNdd.ineligibleNextDayWindow');
  return t('ring.sddNdd.ineligibleSameDayDelivery');
};

export const DeliveryEtaBadge = ({ sddNdd }: { sddNdd: SddNddController }) => {
  const t = useTranslations('ring');
  // No cancel affordance by design: the last checked pincode stays
  // persisted (sessionStorage) and Change is the only way to revisit it.
  const { state, startCheck, setPincodeInput, submitPincode, changePincode } =
    sddNdd;

  const [zapPlayToken, setZapPlayToken] = useState(0);
  const prevStatusRef = useRef<SddNddState['status'] | null>(null);
  const pulseZap = () => setZapPlayToken((token) => token + 1);

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = state.status;
    if (prev !== null && prev !== 'eligible' && state.status === 'eligible') {
      setZapPlayToken((token) => token + 1);
    }
  }, [state.status]);

  if (state.status === 'hidden') return null;

  const placeholder =
    'placeholder' in state && state.placeholder
      ? state.placeholder
      : t('ring.sddNdd.placeholder');

  const isEligible = state.status === 'eligible';
  const isLoading = state.status === 'loading';
  const isIneligible = state.status === 'ineligible';
  const appendTimer =
    !isIneligible && sddWindowShowsTimer(state.deliveryWindow);
  const timerDeadline = appendTimer ? sddTimerDeadline(state) : undefined;

  // Title is always API copy: campaigns.delivery.title while idle /
  // editing / loading / error, serviceability data.title after a result.
  const title = ensurePeriod(
    isEligible || isIneligible
      ? state.title
      : 'bannerTitle' in state
      ? state.bannerTitle
      : '',
  );
  const [titleLead, titleRest] = splitTitle(title);

  // Ineligible keeps its pincode in the field (mirror of the eligible
  // city pill) so users see what they checked; Check re-submits it.
  const fieldPincode =
    state.status === 'editing' ||
    state.status === 'loading' ||
    state.status === 'error' ||
    state.status === 'ineligible'
      ? state.pincode
      : '';

  const meta =
    state.status === 'editing' && state.fieldError
      ? { text: state.fieldError, alert: true }
      : state.status === 'error'
      ? { text: state.message, alert: true }
      : state.status === 'ineligible'
      ? {
          text: ineligibleMetaCopy(state.deliveryWindow, t),
          alert: true,
        }
      : state.status === 'idle' ||
        state.status === 'editing' ||
        state.status === 'loading'
      ? { text: t('ring.sddNdd.applicableSelectCities'), alert: false }
      : null;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isLoading) return;
    if (state.status === 'idle') {
      startCheck();
      return;
    }
    submitPincode();
  };

  return (
    <div className={styles.badge} data-testid={`sdd-ndd-badge-${state.status}`}>
      <div className={styles.card}>
        {title ? (
          <div className={styles.titleRow}>
            <ZapIcon playToken={zapPlayToken} />
            <p className={styles.title}>
              {state.status === 'eligible' ? (
                <>
                  <span className={styles.shimmerTitle} key={title}>
                    <strong>{titleLead}</strong>
                    {titleRest ? <span> {titleRest}</span> : null}
                  </span>
                  {appendTimer ? (
                    <SddCountdown deadline={timerDeadline} />
                  ) : null}
                </>
              ) : (
                <>
                  <strong>{titleLead}</strong>
                  {titleRest ? <span> {titleRest}</span> : null}
                  {appendTimer ? (
                    <SddCountdown deadline={timerDeadline} />
                  ) : null}
                </>
              )}
            </p>
          </div>
        ) : null}

        {state.status === 'eligible' ? (
          <div className={styles.resultPill}>
            <span className={styles.location}>
              {state.city || state.pincode}
            </span>
            <button
              className={styles.change}
              type="button"
              onClick={() => {
                pulseZap();
                changePincode();
              }}
            >
              {t('ring.sddNdd.change')}
            </button>
          </div>
        ) : (
          <form
            className={styles.pincodeForm}
            onSubmit={onSubmit}
            aria-busy={isLoading}
          >
            <input
              className={styles.pincodeInput}
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={6}
              value={fieldPincode}
              placeholder={placeholder}
              aria-label={placeholder}
              aria-invalid={
                state.status === 'editing' ? Boolean(state.fieldError) : false
              }
              autoFocus={state.status === 'editing'}
              readOnly={isLoading}
              // Ineligible keeps the checked pincode in the field; selecting
              // it on focus lets the user type a new one without deleting.
              onFocus={(event) => {
                if (state.status === 'ineligible') event.currentTarget.select();
              }}
              onChange={(event) => setPincodeInput(event.target.value)}
            />
            <button
              type="submit"
              className={`${styles.check}${
                isLoading ? ` ${styles.isLoading}` : ''
              }`}
              disabled={isLoading}
              aria-label={
                isLoading ? t('ring.sddNdd.checking') : t('ring.sddNdd.check')
              }
            >
              {isLoading ? (
                <span className={styles.loadingDots} aria-hidden>
                  <span />
                  <span />
                  <span />
                </span>
              ) : (
                t('ring.sddNdd.check')
              )}
            </button>
          </form>
        )}

        {meta ? (
          <p className={styles.metaLine} role="status">
            {meta.alert ? <AlertIcon /> : null}
            <span>{meta.text}</span>
          </p>
        ) : null}
      </div>
    </div>
  );
};
